import { useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { CartContext } from '../context/CartContext';
import Toast from './Toast';
import '../styles/ProductCard.css';

const ProductCard = ({ product }) => {
  // Get variants and sort by weight (smallest first)
  const variants = product.variants || [];
  const sortedVariants = [...variants].sort((a, b) => parseFloat(a.weight) - parseFloat(b.weight));

  // Set initial variant to the smallest weight
  const initialVariant = sortedVariants.length > 0 ? sortedVariants[0] : null;

  const [selectedVariant, setSelectedVariant] = useState(initialVariant);
  const [quantity, setQuantity] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [toast, setToast] = useState(null);
  const { addToCart } = useContext(CartContext);

  const handleQuantityChange = (change) => {
    const newQuantity = quantity + change;
    if (newQuantity >= 0 && newQuantity <= 99) {
      setQuantity(newQuantity);
    }
  };

  const handleVariantChange = (e) => {
    const variantId = e.target.value;
    const variant = sortedVariants.find(v => v._id === variantId);
    setSelectedVariant(variant);
  };

  const handleAddToCart = () => {
    if (quantity === 0) {
      setToast({ message: 'Please select quantity', type: 'warning' });
      return;
    }

    // Create product with selected variant
    const productToAdd = {
      ...product,
      selectedVariant: selectedVariant,
      variantPrice: selectedVariant?.price || product.price,
      variantWeight: selectedVariant?.weight,
      price: selectedVariant?.price || product.price
    };

    // Add to cart with the selected quantity
    addToCart(productToAdd, quantity);

    const productName = product.productName || product.name;
    const weightText = selectedVariant ? ` (${selectedVariant.weight} ${normalizeUnit(selectedVariant.unit)})` : '';
    setToast({
      message: `${quantity} × ${productName}${weightText} added to cart!`,
      type: 'success'
    });

    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      setQuantity(0);
    }, 2000);
  };

  // Handle both API formats
  const productId = product._id || product.id;
  const productName = product.productName || product.name;
  const productImage = product.productImage || product.image;

  // Normalize unit names
  const normalizeUnit = (unit) => {
    if (!unit) return unit;
    if (unit.toLowerCase() === 'gm') return 'gms';
    return unit;
  };

  // Use variant price if available, otherwise use product price
  const displayPrice = selectedVariant?.price || product.price;

  // Construct image URL for backend uploads
  const UPLOAD_URL = process.env.REACT_APP_UPLOAD_URL;
  const imageUrl = productImage?.startsWith('uploads')
    ? `${UPLOAD_URL}/${productImage.replace(/\\/g, '/')}`
    : productImage || '/item-image.png';

  const totalPrice = displayPrice * quantity;
  
  // Check if selected variant is out of stock
  const isOutOfStock = selectedVariant ? selectedVariant.quantity === 0 : false;

  return (
    <>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <div className={`product-card ${isOutOfStock ? 'out-of-stock' : ''}`}>
        <Link to={`/products/${productId}`} className="product-image-link">
          <img src={imageUrl} alt={productName} />
          {isOutOfStock && (
            <div className="stock-badge out-of-stock-badge">
              Out of Stock
            </div>
          )}
        </Link>
        <div className="product-info">
          <Link to={`/products/${productId}`} className="product-title-link">
            <h3>{productName}</h3>
          </Link>
          <div className="price-section" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <p className="unit-price">
              ₹{displayPrice}
              {selectedVariant && <span className="weight-label"> / {selectedVariant.weight}{normalizeUnit(selectedVariant.unit)}</span>}
            </p>
            {quantity > 0 && (
              <p className="total-price">Total: ₹{totalPrice.toFixed(2)}</p>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {/* Weight/Variant Selector */}
            {sortedVariants.length > 0 && (
              <div className="variant-selector" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label>Weight:</label>
                <select value={selectedVariant?._id || ''} onChange={handleVariantChange} style={{marginLeft:'10px'}}>
                  {sortedVariants.map(variant => (
                    <option key={variant._id} value={variant._id}>
                      {variant.weight}{normalizeUnit(variant.unit)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="product-actions">
              <div className="quantity-selector">
                <button
                  className="qty-btn"
                  onClick={() => handleQuantityChange(-1)}
                  disabled={quantity <= 0 || isOutOfStock}
                >
                  -
                </button>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 0;
                    if (val >= 0 && val <= 99) setQuantity(val);
                  }}
                  min="0"
                  max="99"
                  disabled={isOutOfStock} />
                <button
                  className="qty-btn"
                  onClick={() => handleQuantityChange(1)}
                  disabled={quantity >= 99 || isOutOfStock}
                >
                  +
                </button>
              </div>

            </div>
          </div>

          <div className="button-row">
            <button 
              className="btn-add-to-cart" 
              onClick={handleAddToCart}
              disabled={quantity === 0 || isOutOfStock}
            >
              {showSuccess ? '✓ Added!' : isOutOfStock ? 'Out of Stock' : 'Add to Cart'}
            </button>

            <Link to={`/products/${productId}`} className="btn-view-details">
              View Details
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductCard;
